import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export enum ConnectivityStatus {
  ONLINE = 'connected',
  CONNECTING = 'connecting',
  OFFLINE = 'disconnected',
}

// 'browser' = navigator.onLine / online-offline events (default). 'native' = the webview native
// layer's CONNECTIVITY_CHANGED, authoritative once seen.
export type NetworkOnlineSource = 'browser' | 'native';

export const useGenericAppStore = defineStore('genericAppStore', () => {
  // networkOnline: device network. streamConnected: whether our SSE stream to the backend is up.
  const networkOnline = ref(true);
  const streamConnected = ref(true);
  const showBetaNotice = ref(false);

  // Browser signal drives connectivity by default (old native apps don't emit CONNECTIVITY_CHANGED,
  // and the standalone web app has no native layer). Once a native update arrives we trust it for
  // the rest of the session and ignore the browser events.
  let networkOnlineSource: NetworkOnlineSource = 'browser';

  const setNetworkOnline = (online: boolean, source: NetworkOnlineSource = 'browser'): void => {
    if (source === 'browser' && networkOnlineSource === 'native') {
      return;
    }

    networkOnlineSource = source;
    networkOnline.value = online;
  };

  const setStreamConnected = (connected: boolean): void => {
    streamConnected.value = connected;
  };

  const isOnline = computed(() => networkOnline.value);

  const connectivityStatus = computed<ConnectivityStatus>(() => {
    if (!networkOnline.value) {
      return ConnectivityStatus.OFFLINE;
    }

    if (!streamConnected.value) {
      return ConnectivityStatus.CONNECTING;
    }

    return ConnectivityStatus.ONLINE;
  });

  const setShowBetaNotice = (value: boolean): void => {
    showBetaNotice.value = value;
  };

  return {
    networkOnline,
    streamConnected,
    connectivityStatus,
    isOnline,
    showBetaNotice,
    setShowBetaNotice,
    setNetworkOnline,
    setStreamConnected,
  };
});
